import { msg } from "@lingui/core/macro";
import { createComputed, createEffect, createSignal, Show, untrack } from "solid-js";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useDrizzle } from "@/hooks/use-drizzle";
import { createPowerSyncQuery } from "@listto/shared/solid";
import { type getMyDayTodosCountQuery, type getTodosCountQuery, queryKeys } from "@listto/shared";
import { useI18n } from "@/providers/i18n-provider";
import { keepPreviousData } from "@tanstack/solid-query";
import type { PowerSyncDrizzleDB } from "@listto/powersync";
import { Tooltip, TooltipContent, TooltipTrigger } from "@fleet/ui";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@fleet/utils";
import { OFFLINE_CACHE_STALE_TIME_MS } from "@/lib/constants";

interface TaskCountBadgeProps {
  listId: string;
  userId: string | undefined;
  countQueryFn: (
    db: PowerSyncDrizzleDB,
    userId: string,
  ) => ReturnType<typeof getTodosCountQuery> | ReturnType<typeof getMyDayTodosCountQuery>;
}

export function TaskCountBadge(props: TaskCountBadgeProps) {
  const { locale, t } = useI18n();
  const { db } = usePowerSyncContext();
  const drizzle = useDrizzle();
  const [currentCount, setCurrentCount] = createSignal<number | undefined>(undefined);

  const todoCount = createPowerSyncQuery<{ count: number }>({
    queryKey: () => queryKeys.todos.listCount({ listId: props.listId, userId: props.userId }),
    db: () => db(),
    queryFn: () => {
      const dbInstance = drizzle();
      const uid = props.userId;
      return dbInstance && uid ? props.countQueryFn(dbInstance, uid) : null;
    },
    enabled: () => Boolean(props.userId) && Boolean(drizzle()),
    placeholderData: keepPreviousData,
    staleTime: OFFLINE_CACHE_STALE_TIME_MS,
  });

  createComputed(() => {
    void props.listId;
    untrack(() => {
      setCurrentCount(undefined);
    });
  });

  createEffect(() => {
    const { data } = todoCount;
    const isPlaceholder = todoCount.isPlaceholderData;
    const currentListId = props.listId;

    if (data && !isPlaceholder) {
      untrack(() => {
        if (currentListId === props.listId) {
          setCurrentCount(data[0]?.count ?? 0);
        }
      });
    }
  });

  const isCompact = () => {
    const count = currentCount();
    return count !== undefined && count >= COMPACT_THRESHOLD;
  };

  const fullLabel = () => {
    const count = currentCount() ?? 0;
    return `${count.toLocaleString(locale())} ${t(msg`tasks`)}`;
  };

  return (
    <Show
      when={isCompact()}
      fallback={
        <span>
          {currentCount() ?? 0} {t(msg`tasks`)}
        </span>
      }
    >
      <Tooltip openDelay={350} closeDelay={100}>
        <TooltipTrigger class="focus-visible:outline-none select-none cursor-help">
          <span class="border-b border-dashed border-current pb-0.5">
            {formatCompactNumber(currentCount() ?? 0, locale())} {t(msg`tasks`)}
          </span>
        </TooltipTrigger>
        <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
          {fullLabel()}
        </TooltipContent>
      </Tooltip>
    </Show>
  );
}
